/**
 * GlobeViewer interactions mixin: centering/selection, raycasting, clickability,
 * regulation level, hover/click handling, and pointer/wheel event wiring.
 * Methods run with `this` bound to a GlobeViewer instance; composed onto the
 * prototype in globe-viewer.ts. Extracted verbatim (Phase 2, PR-C). @ts-nocheck.
 */
/* eslint-disable */
// @ts-nocheck
import * as THREE from 'three'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { geoToCartesian, STATE_NAME_TO_CODE } from './geo-helpers'

export const interactionMethods = {
      centerOnCountry(countryCode, regionType, onNearCenter) {
        let mesh = null;
        
        // If regionType is specified, search that type first
        if (regionType === 'state') {
          // Search states first for state type
          mesh = this.meshes.stateBorders.find(m => 
            m.userData.code && m.userData.code.toUpperCase() === countryCode.toUpperCase()
          );
          // Fall back to countries if not found
          if (!mesh) {
            mesh = this.meshes.countryBorders.find(m => 
              m.userData.code && m.userData.code.toUpperCase() === countryCode.toUpperCase()
            );
          }
        } else {
          // Default: search countries first, then states
          mesh = this.meshes.countryBorders.find(m => 
            m.userData.code && m.userData.code.toUpperCase() === countryCode.toUpperCase()
          );
          if (!mesh) {
            mesh = this.meshes.stateBorders.find(m => 
              m.userData.code && m.userData.code.toUpperCase() === countryCode.toUpperCase()
            );
          }
        }
        
        if (mesh && mesh.userData.centroid) {
          const code = (mesh.userData.code || '').toUpperCase();
          const override = this.CENTER_FOCUS_OVERRIDES.get(code);
          const [lon, lat] = override != null ? override : mesh.userData.centroid;
          this.animateToPosition(lon, lat, onNearCenter);
          
          // Apply selection highlighting
          this.selectRegion(mesh);
        }
      }
      
      // Apply selection highlighting to a border mesh
,
      selectRegion(mesh) {
        // Clear previous selection
        if (this.selectedMesh && this.selectedMesh !== mesh) {
          this.clearSelection();
        }
        
        this.selectedMesh = mesh;
        
        // Cancel any active transition on this mesh to prevent overwrites
        this.transitions.delete(mesh);
        
        // Clear hoveredMesh if it's the same region (by code) to prevent re-triggering hover
        if (this.isSameRegion(this.hoveredMesh, mesh)) {
          this.hoveredMesh = null;
        }
        
        const style = this.getRegionInteractiveStyle(mesh);
        if (mesh.material) {
          mesh.material.linewidth = this.SELECTED_LINE_WIDTH;
          mesh.material.color.setHex(this.HOVER_BORDER_COLOR);
          mesh.material.opacity = 1.0;
          mesh.material.needsUpdate = true;
          mesh.renderOrder = this.ACTIVE_RENDER_ORDER + 1;
        }
        if (style.useSubregionFills) {
          this.setSubregionFillsStyle(mesh.userData.code, this.HOVER_FILL_OPACITY, this.HOVER_FILL_COLOR);
        } else {
          const fillMesh = mesh.userData.fillMesh;
          if (fillMesh && fillMesh.material) {
            fillMesh.material.color.setHex(this.HOVER_FILL_COLOR);
            fillMesh.material.opacity = style.selectFillOpacity;
            fillMesh.material.needsUpdate = true;
          }
        }
        
        this.needsRender = true;
      }
      
      // Clear selection highlighting from the currently selected mesh
,
      clearSelection() {
        if (this.selectedMesh && this.selectedMesh.material) {
          this.selectedMesh.material.linewidth = this.selectedMesh.userData.originalLineWidth || this.DEFAULT_LINE_WIDTH;
          this.selectedMesh.material.color.setHex(this.selectedMesh.userData.originalColor);
          this.selectedMesh.material.opacity = this.selectedMesh.userData.originalOpacity;
          this.selectedMesh.material.needsUpdate = true;
          this.selectedMesh.renderOrder = this.ACTIVE_RENDER_ORDER;
          const style = this.getRegionInteractiveStyle(this.selectedMesh);
          if (style.useSubregionFills) {
            this.setSubregionFillsStyle(this.selectedMesh.userData.code, this.subregionsActive ? this.ACTIVE_FILL_OPACITY : 0, this.subregionsActive ? this.ACTIVE_FILL_COLOR : null);
          } else {
            const fillMesh = this.selectedMesh.userData.fillMesh;
            if (fillMesh && fillMesh.material) {
              const op = fillMesh.userData.originalFillOpacity != null ? fillMesh.userData.originalFillOpacity : 0;
              const col = fillMesh.userData.originalFillColor != null ? fillMesh.userData.originalFillColor : this.INACTIVE_BORDER_COLOR;
              fillMesh.material.opacity = op;
              fillMesh.material.color.setHex(col);
              fillMesh.material.needsUpdate = true;
            }
          }
        }
        this.selectedMesh = null;
        this.needsRender = true;
      }

      // Animate globe rotation to center on a specific lon/lat
      // onNearCenter: optional callback fired once when region is visually near-center
,
      animateToPosition(lon, lat, onNearCenter) {
        // Convert lon/lat to rotation values
        // At rotation=0, the camera (on Z+) sees lon=-90° (which is on Z+ axis per geoToCartesian)
        // To bring longitude L to face camera: rotation = -(90 + L)
        // The globe's Y rotation corresponds to longitude
        // The globe's X rotation corresponds to latitude  
        const targetRotX = -(90 + lon); // Offset by -90 because lon=-90 is the default center
        const targetRotY = -lat; // Latitude maps to X rotation (inverted)
        
        const startRotX = this.rotation.x;
        const startRotY = this.rotation.y;
        
        // Normalize angle differences to find shortest path (-180 to 180)
        // This prevents wild spins when rotation.x has accumulated from auto-spin
        let deltaX = targetRotX - startRotX;
        while (deltaX > 180) deltaX -= 360;
        while (deltaX < -180) deltaX += 360;
        
        let deltaY = targetRotY - startRotY;
        while (deltaY > 180) deltaY -= 360;
        while (deltaY < -180) deltaY += 360;
        
        const duration = 800; // ms
        const startTime = Date.now();
        let nearCenterFired = false;
        const nearCenterThreshold = 0.35; // At 35% progress, eased ~= 0.73 (region approaching center)
        
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Ease-out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          
          this.rotation.x = startRotX + deltaX * eased;
          this.rotation.y = startRotY + deltaY * eased;
          this.needsRender = true;
          
          // Fire the near-center callback once when threshold is crossed
          if (!nearCenterFired && progress >= nearCenterThreshold) {
            nearCenterFired = true;
            if (onNearCenter) onNearCenter();
          }
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        
        animate();
      }

,
      getMousePosition(e) {
        const rect = this.container.getBoundingClientRect();
        return {
          x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
          y: -((e.clientY - rect.top) / rect.height) * 2 + 1
        };
      }

,
      performRaycast(e) {
        const mouse = this.getMousePosition(e);
        this.raycaster.setFromCamera(mouse, this.camera);
        
        // First, check if we hit the globe surface to use as an occluder
        // This prevents clicking on regions that are "behind" the globe
        let maxDistance = Infinity;
        if (this.meshes.globeFill) {
          const globeIntersects = this.raycaster.intersectObject(this.meshes.globeFill, false);
          if (globeIntersects.length > 0) {
            // Add small buffer since fills are slightly above globe surface (radius 1.004 vs 1.0)
            maxDistance = globeIntersects[0].distance + 0.02;
          }
        }
        
        // Check fill meshes for hit detection (they cover the entire region area)
        // This allows clicking anywhere inside a region, not just on the border line
        const allFills = [...this.meshes.countryFills, ...this.meshes.stateFills];
        const intersects = this.raycaster.intersectObjects(allFills, false);
        
        // Only return intersections that are in front of the globe surface
        const frontIntersects = intersects.filter(i => i.distance < maxDistance);
        
        if (frontIntersects.length > 0) {
          // When jurisdiction is All (platform) and Age Assurance: prefer subnational (state) over national (country). Otherwise prefer country over state (e.g. Digital IDs).
          // Always prefer specific countries over the EU aggregate polygon when both are hit.
          let firstClickableCountry = null;
          let firstClickableCountryNonEU = null;
          let firstClickableState = null;
          let firstClickableAny = null;
          for (const i of frontIntersects) {
            const fill = i.object;
            const mesh = fill.userData.borderMesh || fill;
            if (!this.isClickable(mesh)) continue;
            if (mesh.userData.type === 'country') {
              if (!firstClickableCountry) firstClickableCountry = mesh;
              if (!firstClickableCountryNonEU && mesh.userData.code !== 'EU') firstClickableCountryNonEU = mesh;
            } else if (mesh.userData.type === 'state') {
              if (!firstClickableState) firstClickableState = mesh;
            }
            if (!firstClickableAny) firstClickableAny = mesh;
          }
          if (this.regulationLevel === 'platform' && this.getCurrentDataType() === 'age_assurance') {
            return firstClickableState || firstClickableCountryNonEU || firstClickableCountry;
          }
          return firstClickableCountryNonEU || firstClickableCountry || firstClickableAny;
        }
        
        return null;
      }

,
      isClickable(mesh) {
        if (!mesh || !mesh.userData) return false;
        const dataType = this.getCurrentDataType();
        
        if (mesh.userData.type === 'country') {
          if (this.regulationLevel === 'regional' && dataType === 'age_assurance') return false;
          if (this.regulationLevel === 'eu' && dataType === 'age_assurance') return mesh.userData.code === 'EU';
          if (this.regulationLevel === 'national' && dataType === 'age_assurance' && mesh.userData.code === 'EU') return false;
          if (dataType === 'digital_ids') {
            return this.clickableCountriesDigitalIds.has(mesh.userData.code);
          }
          return this.clickableCountries.has(mesh.userData.code);
        } else if (mesh.userData.type === 'state') {
          // States only clickable in Age Assurance when jurisdiction is not National or EU
          if (dataType === 'digital_ids') return false;
          if (this.regulationLevel === 'national' || this.regulationLevel === 'eu') return false;
          return this.clickableStates.has(mesh.userData.code);
        }
        return false;
      }

,
      setRegulationLevel(level) {
        // Set the regulation level: 'platform', 'national', or 'regional'
        this.regulationLevel = level;
        
        // Save to localStorage
        localStorage.setItem('regulationLevel', level);
        
        // Clear any current hover state
        if (this.hoveredMesh) {
          this.hoveredMesh.material.opacity = this.hoveredMesh.userData.originalOpacity;
          this.hoveredMesh = null;
        }
        this.renderer.domElement.style.cursor = 'grab';
        
        // Update border colors based on mode (with smooth transition)
        this.applyModeColors(true);
        
        this.needsRender = true;
      }

,
      handleHoverEffect(e) {
        const intersectedMesh = this.performRaycast(e);
        const now = Date.now();
        
        // Apply hover effect if clickable (but not if it's the selected mesh)
        if (intersectedMesh && this.isClickable(intersectedMesh)) {
          // Update last hover time whenever we detect a clickable region
          this.lastHoverTime = now;
          
          // Same region: skip border/fill updates, only update tooltip position and cursor
          if (this.isSameRegion(this.hoveredMesh, intersectedMesh)) {
            this.showTooltip(intersectedMesh.userData.name, e.clientX, e.clientY);
            this.renderer.domElement.style.cursor = 'pointer';
            return;
          }
          
          // Don't apply hover effect to the selected mesh - it already has selection styling
          // Use code comparison to handle cases where objects might differ
          if (!this.isSameRegion(intersectedMesh, this.selectedMesh)) {
            // Clear previous hover effect if different mesh (with transition)
            // But don't clear if previous hovered mesh is the selected mesh
            if (this.hoveredMesh && !this.isSameRegion(this.hoveredMesh, this.selectedMesh)) {
              this.hoveredMesh.material.opacity = this.hoveredMesh.userData.originalOpacity;
              this.hoveredMesh.material.linewidth = this.hoveredMesh.userData.originalLineWidth || this.DEFAULT_LINE_WIDTH;
              this.startTransition(
                this.hoveredMesh,
                this.hoveredMesh.userData.originalColor
              );
              const prevStyle = this.getRegionInteractiveStyle(this.hoveredMesh);
              if (prevStyle.useSubregionFills) {
                this.setSubregionFillsStyle(this.hoveredMesh.userData.code, this.subregionsActive ? this.ACTIVE_FILL_OPACITY : 0, this.subregionsActive ? this.ACTIVE_FILL_COLOR : null);
              } else {
                const prevFill = this.hoveredMesh.userData.fillMesh;
                if (prevFill && prevFill.material) {
                  const op = prevFill.userData.originalFillOpacity != null ? prevFill.userData.originalFillOpacity : 0;
                  const col = prevFill.userData.originalFillColor != null ? prevFill.userData.originalFillColor : this.INACTIVE_BORDER_COLOR;
                  prevFill.material.opacity = op;
                  prevFill.material.color.setHex(col);
                  prevFill.material.needsUpdate = true;
                }
              }
            }
            
            this.hoveredMesh = intersectedMesh;
            const style = this.getRegionInteractiveStyle(this.hoveredMesh);
            this.hoveredMesh.renderOrder = this.ACTIVE_RENDER_ORDER + 1;
            this.hoveredMesh.material.opacity = 1.0;
            this.startTransition(
              this.hoveredMesh,
              this.HOVER_BORDER_COLOR
            );
            if (style.useSubregionFills) {
              this.setSubregionFillsStyle(this.hoveredMesh.userData.code, this.HOVER_FILL_OPACITY, this.HOVER_FILL_COLOR);
            } else {
              const fillMesh = this.hoveredMesh.userData.fillMesh;
              if (fillMesh && fillMesh.material) {
                fillMesh.material.color.setHex(this.HOVER_FILL_COLOR);
                fillMesh.material.opacity = style.hoverFillOpacity;
                fillMesh.material.needsUpdate = true;
              }
            }
          }
          this.showTooltip(intersectedMesh.userData.name, e.clientX, e.clientY);
          this.renderer.domElement.style.cursor = 'pointer';
        } else {
          // Only clear hover if no detection for 150ms (prevents flicker during spin)
          const hoverTimeout = 150;
          // Don't clear hover effect from the selected mesh (use code comparison)
          if (this.hoveredMesh && !this.isSameRegion(this.hoveredMesh, this.selectedMesh) && (!this.lastHoverTime || now - this.lastHoverTime > hoverTimeout)) {
            this.hideTooltip();
            this.hoveredMesh.material.opacity = this.hoveredMesh.userData.originalOpacity;
            this.hoveredMesh.material.linewidth = this.hoveredMesh.userData.originalLineWidth || this.DEFAULT_LINE_WIDTH;
            this.hoveredMesh.renderOrder = this.ACTIVE_RENDER_ORDER;
            this.startTransition(
              this.hoveredMesh,
              this.hoveredMesh.userData.originalColor
            );
            const style = this.getRegionInteractiveStyle(this.hoveredMesh);
            if (style.useSubregionFills) {
              this.setSubregionFillsStyle(this.hoveredMesh.userData.code, this.subregionsActive ? this.ACTIVE_FILL_OPACITY : 0, this.subregionsActive ? this.ACTIVE_FILL_COLOR : null);
            } else {
              const fillMesh = this.hoveredMesh.userData.fillMesh;
              if (fillMesh && fillMesh.material) {
                const op = fillMesh.userData.originalFillOpacity != null ? fillMesh.userData.originalFillOpacity : 0;
                const col = fillMesh.userData.originalFillColor != null ? fillMesh.userData.originalFillColor : this.INACTIVE_BORDER_COLOR;
                fillMesh.material.opacity = op;
                fillMesh.material.color.setHex(col);
                fillMesh.material.needsUpdate = true;
              }
            }
            this.hoveredMesh = null;
            this.renderer.domElement.style.cursor = this.isDragging ? 'grabbing' : 'grab';
          } else if (!this.hoveredMesh) {
            this.hideTooltip();
          }
        }
      }

      // BUG: Click detection works, hover highlighting works, but modal display is inconsistent
      // Modal fetch succeeds (/region/au returns 200) but modal doesn't always show on screen
      // Issue may be related to z-index, timing, or modal.classList.add('active') not triggering display
,
      handleClick(e) {
        const intersectedMesh = this.performRaycast(e);
        if (intersectedMesh && this.isClickable(intersectedMesh)) {
          const userData = intersectedMesh.userData;
          if (userData.type === 'country') {
            if (window.openDrawer) {
              window.openDrawer('/region/' + userData.code.toLowerCase(), userData.code);
            }
          } else if (userData.type === 'state') {
            if (window.openDrawer) {
              window.openDrawer('/country/us/region/' + userData.code.toLowerCase(), userData.code);
            }
          }
        }
      }

,
      setupEventListeners() {
        const canvas = this.renderer.domElement;

        // Mouse events
        canvas.addEventListener('mousedown', (e) => this.handleMouseDown(e));
        canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
        canvas.addEventListener('mouseup', (e) => this.handleMouseUp(e));
        canvas.addEventListener('mouseleave', () => this.handleMouseLeave());
        canvas.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });

        // Resize
        window.addEventListener('resize', () => this.handleResize());

        // Touch events for mobile
        canvas.addEventListener('touchstart', (e) => {
          e.preventDefault();
          if (e.touches.length === 2) {
            // Start pinch-to-zoom
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            this.lastPinchDist = Math.sqrt(dx * dx + dy * dy);
          } else if (e.touches.length === 1) {
            this.lastPinchDist = null;
            const touch = e.touches[0];
            this.handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
          }
        });

        canvas.addEventListener('touchmove', (e) => {
          e.preventDefault();
          if (e.touches.length === 2 && this.lastPinchDist !== null) {
            // Pinch-to-zoom
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const delta = this.lastPinchDist - dist;
            this.zoom = Math.max(2, Math.min(15, this.zoom + delta * 0.02));
            this.lastPinchDist = dist;
            this.needsRender = true;
          } else if (e.touches.length === 1 && this.lastPinchDist === null) {
            const touch = e.touches[0];
            this.handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
          }
        });

        canvas.addEventListener('touchend', (e) => {
          if (e.touches.length < 2) {
            this.lastPinchDist = null;
          }
          if (e.touches.length === 0) {
            const touch = e.changedTouches[0];
            this.handleMouseUp({ clientX: touch.clientX, clientY: touch.clientY });
          }
        });
      }

,
      handleMouseDown(e) {
        this.isDragging = true;
        this.mouseDownPos = { x: e.clientX, y: e.clientY };
        this.mouse.lastX = e.clientX;
        this.mouse.lastY = e.clientY;
        this.velocity = { x: 0, y: 0 }; // Reset velocity on new drag
        this.velocityHistory = []; // Clear velocity history
        this.lastDragTime = performance.now();
        this.renderer.domElement.style.cursor = 'grabbing';
      }

,
      handleMouseMove(e) {
        // Store the last mouse position for continuous hover detection during spin
        this.lastMousePos = { clientX: e.clientX, clientY: e.clientY };
        
        if (this.isDragging) {
          const now = performance.now();
          const dt = Math.max(1, now - this.lastDragTime); // Time since last move in ms
          
          const deltaX = e.clientX - this.mouse.lastX;
          const deltaY = e.clientY - this.mouse.lastY;

          this.rotation.x += deltaX * 0.5;
          this.rotation.y = Math.max(-90, Math.min(90, this.rotation.y - deltaY * 0.5));

          // Track velocity for inertia with time-weighted history
          const vx = (deltaX * 0.5) / dt * 16; // Normalize to ~60fps
          const vy = -(deltaY * 0.5) / dt * 16;
          
          // Add to velocity history for smoothing
          this.velocityHistory.push({ x: vx, y: vy, time: now });
          if (this.velocityHistory.length > this.velocityHistoryMax) {
            this.velocityHistory.shift();
          }

          this.mouse.lastX = e.clientX;
          this.mouse.lastY = e.clientY;
          this.lastDragTime = now;
        } else {
          // Throttle hover to at most once per frame (handled in animate())
          this.hoverPending = true;
        }
      }

,
      handleMouseUp(e) {
        this.isDragging = false;
        
        // Calculate smoothed velocity from history with time-weighting
        // More recent samples get higher weight for natural "flick" feel
        if (this.velocityHistory.length > 0) {
          let totalWeight = 0;
          let weightedX = 0;
          let weightedY = 0;
          const now = performance.now();
          
          for (let i = 0; i < this.velocityHistory.length; i++) {
            const sample = this.velocityHistory[i];
            const age = now - sample.time;
            // Exponential decay weight - recent samples matter more
            // Samples older than 100ms get very low weight
            const weight = Math.exp(-age / 50);
            weightedX += sample.x * weight;
            weightedY += sample.y * weight;
            totalWeight += weight;
          }
          
          if (totalWeight > 0) {
            this.velocity.x = weightedX / totalWeight;
            this.velocity.y = weightedY / totalWeight;
          }
        }
        
        // Only trigger click if mouse didn't move much (distinguish from drag)
        const dx = e.clientX - this.mouseDownPos.x;
        const dy = e.clientY - this.mouseDownPos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 5) {
          this.handleClick(e);
        }
        
        this.renderer.domElement.style.cursor = 'grab';
        // Re-check hover state next frame (coalesced in animate())
        this.lastMousePos = { clientX: e.clientX, clientY: e.clientY };
        this.hoverPending = true;
      }

,
      handleMouseLeave() {
        this.isDragging = false;
        this.lastMousePos = null; // Clear mouse tracking when leaving container
        this.hoverPending = false;
        this.lastHoverTime = 0; // Clear hover tracking
        if (this.hoveredMesh) {
          this.hoveredMesh.material.opacity = this.hoveredMesh.userData.originalOpacity;
          this.hoveredMesh.material.color.setHex(this.hoveredMesh.userData.originalColor);
          this.hoveredMesh.material.needsUpdate = true;
          const style = this.getRegionInteractiveStyle(this.hoveredMesh);
          if (style.useSubregionFills) {
            this.setSubregionFillsStyle(this.hoveredMesh.userData.code, this.subregionsActive ? this.ACTIVE_FILL_OPACITY : 0, this.subregionsActive ? this.ACTIVE_FILL_COLOR : null);
          } else {
            const fillMesh = this.hoveredMesh.userData.fillMesh;
            if (fillMesh && fillMesh.material) {
              const op = fillMesh.userData.originalFillOpacity != null ? fillMesh.userData.originalFillOpacity : 0;
              const col = fillMesh.userData.originalFillColor != null ? fillMesh.userData.originalFillColor : this.INACTIVE_BORDER_COLOR;
              fillMesh.material.opacity = op;
              fillMesh.material.color.setHex(col);
              fillMesh.material.needsUpdate = true;
            }
          }
          this.hoveredMesh = null;
        }
        this.hideTooltip();
        this.renderer.domElement.style.cursor = 'grab';
      }

,
      handleWheel(e) {
        e.preventDefault();

        const zoomSpeed = 0.001;
        const delta = e.deltaY * zoomSpeed;

        this.zoom = Math.max(2, Math.min(15, this.zoom + delta));
        this.needsRender = true;
      }

,
}
